import PropTypes from 'prop-types';
import { useReactToPrint } from 'react-to-print';
import React, { useEffect, useRef, useState } from 'react';
import { Dialog, Box } from '@mui/material';
import { QUILL_PRINT_CONTENT_CSS } from 'components/QuillEditor/quillContentStyles';

export default function BasePrintModalThermal({ open, onClose, children, title }) {
  const componentRef = useRef();
  const hasPrintedRef = useRef(false);
  const [pageStyle, setPageStyle] = useState(
    `@page { size: 72mm 120mm; margin: 0mm; } body { margin: 0; font-family: Arial, sans-serif; font-size: 9px; } ${QUILL_PRINT_CONTENT_CSS}`
  );
  const [isReadyToPrint, setIsReadyToPrint] = useState(false);

  const pxToMm = (px) => (px * 25.4) / 96;

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: title || 'ThermalPrint',
    pageStyle,
    onAfterPrint: onClose,
  });

  useEffect(() => {
    if (open) {
      hasPrintedRef.current = false;
      setIsReadyToPrint(false);
      const timeout = setTimeout(() => {
        const contentHeightPx = Math.ceil(
          componentRef.current?.getBoundingClientRect?.().height || componentRef.current?.scrollHeight || 0
        );
        const heightMm = Math.max(60, Math.ceil(pxToMm(contentHeightPx) + 12));
        setPageStyle(
          `@page { size: 72mm ${heightMm}mm; margin: 0mm; } body { margin: 0; font-family: Arial, sans-serif; font-size: 9px; } ${QUILL_PRINT_CONTENT_CSS}`
        );
        setIsReadyToPrint(true);
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [open]);

  useEffect(() => {
    if (open && isReadyToPrint && !hasPrintedRef.current) {
      hasPrintedRef.current = true;
      const timeout = setTimeout(() => {
        handlePrint();
        setIsReadyToPrint(false);
      }, 80);
      return () => clearTimeout(timeout);
    }
  }, [open, isReadyToPrint, handlePrint]);

  return (
    <Dialog open={open} onClose={onClose}>
      <Box
        ref={componentRef}
        sx={{
          width: '72mm',
          fontSize: '9px',
          '@media print': {
            fontSize: '9px',
            padding: 0,
            margin: 0,
            breakInside: 'avoid-page',
            pageBreakInside: 'avoid',
          },
          '& .MuiTableCell-root': {
            padding: '2px',
            fontSize: '9px'
          },
          '&, & *': {
            breakInside: 'avoid-page',
            pageBreakInside: 'avoid'
          }
        }}
      >
        {children}
      </Box>
    </Dialog>
  );
}

BasePrintModalThermal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  title: PropTypes.string
};
